import {
  ButtonV3,
  ModalV3,
  RadioButton,
  TypographyV3,
} from '@jouzen/ecom-components';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { EventType } from '@/analytics/types';
import { useHubspotFormsContext } from '@/app/[locale]/business/_contexts/HubspotFormsContext';
import {
  AMBASSADOR_PROGRAM_FORM_ID,
  API_INTEGRATION_FORM_ID,
} from '@/app/[locale]/business/_data/const';

import HubspotForm from '../HubspotForm';
import type { RadioOption } from './data/radioOptions';
import { legacyRadioOptions, newRadioOptions } from './data/radioOptions';
import { useGate } from 'statsig-react';

export const HUBSPOT_SELECTION_MODAL_ID = 'HUBSPOT_SELECTION';

const HubspotSelectionModal = (): JSX.Element => {
  const radioOptions = useGate('hubspot-v2-forms').value
    ? newRadioOptions
    : legacyRadioOptions;

  const t = useTranslations();
  const [selectedRadio, setSelectedRadio] = useState<RadioOption>(
    radioOptions[0]!,
  );
  const [activeFormId, setActiveFormId] = useState<string>('');
  const { activeModal, setActiveModal } = useHubspotFormsContext();

  const handleClose = () => {
    setSelectedRadio(radioOptions[0]!);
    setActiveFormId('');
    setActiveModal('');
  };

  const handleOnOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      handleClose();
    }
  };

  return (
    <ModalV3
      padding="small"
      size="small"
      isOpen={activeModal === HUBSPOT_SELECTION_MODAL_ID}
      onOpenChange={handleOnOpenChange}
    >
      {activeFormId ? (
        <HubspotForm formId={activeFormId} />
      ) : (
        <div data-cy="business-hubspot-selection-form">
          <TypographyV3
            className="mb-4"
            style={{ color: '#2f4a73' }}
            variant="h5"
          >
            {t('business_connect_modal_heading')}
          </TypographyV3>
          <div className="mb-6 flex flex-col gap-y-4">
            {radioOptions.map((radio) => (
              <div data-cy={radio.label} key={radio.label}>
                <RadioButton
                  checked={selectedRadio.label === radio.label}
                  label={t(radio.label)}
                  onChange={() => {
                    setSelectedRadio(radio);
                  }}
                  style={{ color: '#2f4a73', borderColor: '#2f4a73' }}
                />
              </div>
            ))}
          </div>
          <ButtonV3
            className="px-6"
            style={{ background: '#2f4a73' }}
            variant="primary"
            size="small"
            href={selectedRadio.href}
            target={selectedRadio.href ? '_blank' : null}
            onClick={() => {
              if (selectedRadio.formId === API_INTEGRATION_FORM_ID) {
                void window.ouraAnalytics.track(EventType.LinkClicked, {
                  action: 'go to integration documentation',
                  cta: 'learn more',
                  module: 'connect',
                  location: 'modal',
                  path: selectedRadio.href,
                });

                handleClose();
              } else if (selectedRadio.formId === AMBASSADOR_PROGRAM_FORM_ID) {
                void window.ouraAnalytics.track(EventType.LinkClicked, {
                  action: 'go to ambassador program application',
                  cta: 'ambassador program',
                  module: 'connect',
                  location: 'modal',
                  path: selectedRadio.href,
                });

                handleClose();
              } else {
                setActiveFormId(selectedRadio.formId);
              }
            }}
            data-cy="request-a-quote-modal-next-button"
          >
            {t('next')}
          </ButtonV3>
        </div>
      )}
    </ModalV3>
  );
};

export default HubspotSelectionModal;
